import { makeAutoObservable, runInAction } from "mobx";
import Parse from "parse";
import type { Carpool, CarpoolDirection, CarpoolRole, CreateCarpoolData, JoinRequest } from "./types";

function mapCarpool(obj: Parse.Object): Carpool {
  return {
    objectId: obj.id,
    direction: (obj.get("direction") ?? "to") as CarpoolDirection,
    role: (obj.get("role") ?? "driver") as CarpoolRole,
    driverName: obj.get("driverName") ?? "",
    origin: obj.get("origin") ?? "",
    destination: obj.get("destination") ?? "",
    seats: obj.get("seats") ?? undefined,
    departureTime: obj.get("departureTime") ?? undefined,
    carType: obj.get("carType") ?? undefined,
    joinRequests: obj.get("joinRequests") ?? [],
  };
}

export class CarpoolStore {
  carpools: Carpool[] = [];
  loading = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private byDirectionAndRole(direction: CarpoolDirection, role: CarpoolRole): Carpool[] {
    return this.carpools
      .filter((c) => c.direction === direction && c.role === role)
      .sort((a, b) => (a.departureTime?.getTime() ?? 0) - (b.departureTime?.getTime() ?? 0));
  }

  get toDriverOffers(): Carpool[] {
    return this.byDirectionAndRole("to", "driver");
  }

  get fromDriverOffers(): Carpool[] {
    return this.byDirectionAndRole("from", "driver");
  }

  get toRideRequests(): Carpool[] {
    return this.byDirectionAndRole("to", "rider");
  }

  get fromRideRequests(): Carpool[] {
    return this.byDirectionAndRole("from", "rider");
  }

  get toTrainConnections(): Carpool[] {
    return this.byDirectionAndRole("to", "train");
  }

  get fromTrainConnections(): Carpool[] {
    return this.byDirectionAndRole("from", "train");
  }

  isFull(carpool: Carpool): boolean {
    if (carpool.role !== "driver" || carpool.seats === undefined) return false;
    return carpool.joinRequests.length >= carpool.seats;
  }

  async loadAll() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      const results = await new Parse.Query("SCH_Carpool")
        .ascending("departureTime")
        .find();
      runInAction(() => {
        this.carpools = results.map(mapCarpool);
        this.loading = false;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error = e?.message ?? "Fehler beim Laden";
        this.loading = false;
      });
    }
  }

  async createCarpool(data: CreateCarpoolData) {
    const obj = new Parse.Object("SCH_Carpool");
    obj.set("direction", data.direction);
    obj.set("role", data.role);
    obj.set("driverName", data.driverName);
    obj.set("driverEmail", data.driverEmail);
    obj.set("origin", data.origin);
    obj.set("destination", data.destination);
    if (data.seats !== undefined) obj.set("seats", data.seats);
    if (data.departureTime) obj.set("departureTime", data.departureTime);
    if (data.carType) obj.set("carType", data.carType);
    obj.set("joinRequests", []);
    const saved = await obj.save();
    runInAction(() => {
      this.carpools.push(mapCarpool(saved));
    });
  }

  async contactCarpool(carpoolId: string, requesterName: string, requesterContact: string) {
    const carpool = this.carpools.find((c) => c.objectId === carpoolId);
    if (!carpool) return;

    const newRequest: JoinRequest = {
      name: requesterName,
      requestedAt: new Date().toISOString(),
    };

    const updatedRequests = [...carpool.joinRequests, newRequest];

    runInAction(() => {
      const idx = this.carpools.findIndex((c) => c.objectId === carpoolId);
      if (idx !== -1) {
        this.carpools[idx] = { ...this.carpools[idx], joinRequests: updatedRequests };
      }
    });

    const obj = Parse.Object.fromJSON({ className: "SCH_Carpool", objectId: carpoolId });
    obj.set("joinRequests", updatedRequests);
    await obj.save();

    try {
      await Parse.Cloud.run("sendCarpoolJoinRequest", {
        carpoolId,
        requesterName,
        requesterContact,
      });
    } catch {
      // Cloud function may not be deployed yet — fail silently
    }
  }

  async removeJoinRequest(carpoolId: string, index: number) {
    const carpool = this.carpools.find((c) => c.objectId === carpoolId);
    if (!carpool) return;

    const updatedRequests = carpool.joinRequests.filter((_, i) => i !== index);

    runInAction(() => {
      const idx = this.carpools.findIndex((c) => c.objectId === carpoolId);
      if (idx !== -1) {
        this.carpools[idx] = { ...this.carpools[idx], joinRequests: updatedRequests };
      }
    });

    const obj = Parse.Object.fromJSON({ className: "SCH_Carpool", objectId: carpoolId });
    obj.set("joinRequests", updatedRequests);
    await obj.save();
  }

  async deleteCarpool(carpoolId: string) {
    runInAction(() => {
      this.carpools = this.carpools.filter((c) => c.objectId !== carpoolId);
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_Carpool", objectId: carpoolId });
    await obj.destroy();
  }
}
